import React, { useEffect, useRef } from 'react';
import { useCarouselContext } from '../Carousel.context';
import { useCarouselDotButtons } from './hooks/useCarouselDotButtons';
import { STATIC_STYLES } from './CarouselDotButtons.styles';
import { useVisibleCarouselDots } from './hooks/useVisibleCarouselDots';
import { CarouselDotButton } from './CarouselDotButton/CarouselDotButton';
import { useForkRef, usePrevious } from '../../../hooks';
import { Styler, StylerProps } from '../../Styler';
import { FixMeLater } from '../../../utils';
import { isSmallDot } from './utils/isSmallDot';
import { MIN_VISIBLE } from './CarouselDotButtons.constants';
import { useCulture } from '../../../culture';

export type CarouselDotButtonsProps = StylerProps;

const TRANSITION_DURATION = '300ms';

export const CarouselDotButtons = React.forwardRef(function CarouselDotButtons(
    props: CarouselDotButtonsProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const { emblaApi, userInitiatedRef } = useCarouselContext();
    const { selectedIndex, scrollSnaps, onDotButtonClick } = useCarouselDotButtons(emblaApi);

    const { visibleDots, startIndex, endIndex, visibleIndexes } = useVisibleCarouselDots(selectedIndex, scrollSnaps);

    const { dir } = useCulture();
    const isRtl = dir === 'rtl';

    const rootRef = useRef<HTMLDivElement>(null);
    const handleRef = useForkRef(ref, rootRef);

    const indicatorRef = useRef<HTMLSpanElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const prevSelectedIndex = usePrevious(selectedIndex) || 0;

    // --- Handle dot click ---
    const handleDotClick = (index: number) => {
        userInitiatedRef.current = true;
        onDotButtonClick(index);
    };

    // Effect: When the selected index changes, animate the selected dot indicator to its new position.
    useEffect(() => {
        const root = rootRef.current;
        const indicator = indicatorRef.current;
        const track = trackRef.current;

        if (!indicator || !track || !root) {
            return;
        }

        const shouldAnimate = userInitiatedRef.current;

        // Direction of the dot animation
        const direction = selectedIndex > prevSelectedIndex ? 'forwards' : 'backwards';

        const currentDot = track.children[prevSelectedIndex] as HTMLElement;
        const dotWidth = currentDot.getBoundingClientRect().width; // width of dot excluding margins
        const dotStyles = getComputedStyle(currentDot);
        const dotMargin = parseFloat(dotStyles.marginLeft);
        const totalDotWidth = dotWidth + dotMargin * 2;
        const totalSlides = track.children.length;

        // --- Root width ---
        const rootWidth = visibleDots * totalDotWidth + dotMargin * 2;
        root.style.transition = shouldAnimate ? `width ease-in-out ${TRANSITION_DURATION}` : 'none';
        root.style.width = `${rootWidth}px`;

        // --- Track transform ---
        let trackOffset = 0;
        const triggerSlide = MIN_VISIBLE - 1;
        if (selectedIndex > triggerSlide) {
            trackOffset = (selectedIndex - triggerSlide) * totalDotWidth;
        }
        trackOffset = Math.min(trackOffset, totalSlides * totalDotWidth - visibleDots * totalDotWidth);
        track.style.transition = shouldAnimate ? `transform ease-in-out ${TRANSITION_DURATION}` : 'none';
        track.style.transform = `translate3d(${isRtl ? trackOffset : -trackOffset}px, 0, 0)`;

        // --- Indicator transform and stretch ---
        const indicatorOffset = (isRtl ? -selectedIndex : selectedIndex) * totalDotWidth;
        const indicatorTransform = `translate3d(${indicatorOffset}px, 0, 0)`;

        const indexDiff = Math.abs(selectedIndex - prevSelectedIndex);
        const indicatorStretchedWidth = (indexDiff + 1) * dotWidth + indexDiff * 2 * dotMargin;

        indicator.style.transition = shouldAnimate ? `all ease-in-out ${TRANSITION_DURATION}` : 'none';
        indicator.style.width = shouldAnimate ? `${indicatorStretchedWidth}px` : '';

        // When transitioning backwards , we want to stretch the dot and move simultaneously
        if (direction === 'backwards' || !shouldAnimate) {
            indicator.style.transform = indicatorTransform;
        }

        const handleTransitionEnd = () => {
            // Reset width so the indicator shrinks back to it's original size
            indicator.style.width = '';

            // When transitioning forwards, we move only once stretching has completed
            if (direction === 'forwards') {
                indicator.style.transform = indicatorTransform;
            }
        };

        if (shouldAnimate) {
            indicator.addEventListener('transitionend', handleTransitionEnd, { once: true });
        }

        // Reset the flag after applying
        userInitiatedRef.current = false;
    }, [selectedIndex, prevSelectedIndex, visibleDots, isRtl, userInitiatedRef]);

    return (
        <Styler ref={handleRef} baseStyles={STATIC_STYLES} {...props}>
            <div data-carousel-dots-track-container={true}>
                <div ref={trackRef} data-carousel-dots-track={true}>
                    {scrollSnaps.map((_, index) => (
                        <CarouselDotButton
                            // eslint-disable-next-line react/no-array-index-key
                            key={index.toString()}
                            index={index}
                            isSelected={index === selectedIndex}
                            isSmall={isSmallDot(index, startIndex, endIndex, scrollSnaps.length, isRtl)}
                            isVisible={visibleIndexes[index]}
                            onClick={() => handleDotClick(index)}
                        />
                    ))}
                    <span
                        ref={indicatorRef}
                        data-carousel-dot={true}
                        data-carousel-dot-indicator={true}
                        aria-hidden={true}
                    />
                </div>
            </div>
        </Styler>
    );
});
